import React, { Suspense, useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import s from "./lazy.module.scss";

interface LazyComponentProps {
  Component: React.ComponentType;
  rootMargin?: string | number;
}

const LazyComponent: React.FC<LazyComponentProps> = ({ Component, rootMargin = "400px" }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const observerRef = useRef<IntersectionObserver>();

  useEffect(() => {
    if (!containerRef.current || isLoaded) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsLoaded(true);
          observerRef.current?.disconnect();
        }
      },
      {
        root: null,
        rootMargin: `${rootMargin}px`,
        threshold: 0.01,
      }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rootMargin, isLoaded]);

  return (
    <div ref={containerRef}>
      <Suspense
        fallback={
          <div className={s.loadingContainer}>
            <Skeleton height={400} width="100%" />
          </div>
        }
      >
        {isLoaded ? <Component /> : <Skeleton height={400} width="100%" />}
      </Suspense>
    </div>
  );
};

export default LazyComponent;
